import { useStore } from '../../store/store';
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels, titleDisplayIndex } from '../../utils/titles';
import { isATitleBox, isExcludedChapterTitle } from '../../utils/boxes';
import { TitlesTypes } from '../../constants/constants';
//CSS
import './summarySelector.css';
import _ from 'lodash';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import NavbarSelector from '../Layout/NavbarSelector';
import StackedLabelField from '../common/StackedLabelField';

// Niveaux affichés par un sommaire : ceux qu'il a réglés lui-même, sinon (anciens
// documents, sommaire jamais réglé) ceux de la configuration du document.
export const getSummaryLevels = (summaryBox, summaryConfiguration) =>
  summaryBox?.content?.configuration?.visibleLevels ??
  Object.values(TitlesTypes).filter((type) => summaryConfiguration[type]?.visibility === 'visible');

export const getDisplayableTitles = (boxes, summaryConfiguration, configuration, columns, masks, levels = null) => {
  const isLevelShown = (type) => (levels ? levels.includes(type) : summaryConfiguration[type].visibility);
  return boxes
    .filter((box) => isATitleBox(box.type) && isLevelShown(box.type) && !isExcludedChapterTitle(box, columns, masks, configuration))
    .map((title) => {
      return {
        text: title.content.editorState.getCurrentContent().getPlainText(),
        id: title.id,
        pageNumber: title.pageNumber,
        type: title.type,
        indexTitle: displayTitleIndex(
          titleDisplayIndex(title.content),
          configuration.separatorTitle,
          getTitlesConfig(configuration),
          numberedTitleLevels(configuration),
          configuration.finalSeparatorTitle
        ),
      }
    })
}

// To handle legacy configs with integer and not ids
export const getIdsFromConfig = (startConfigValue, endConfigValue, displayableTitles, allTitles, startFrom, duppedBreakIdx) => {
  if (displayableTitles.length === 0 || allTitles.length === 0) {
    return {
      idStart: 0,
      idEnd: 0
    }
  }
  const getId = (configValue, defaultValue, start = true) => {
    // we use to have number as index for values
    if (configValue && _.isString(configValue)) {
      const title = displayableTitles.find((t) => t.id === configValue);
      if (title) { return configValue }
      const configIndex = allTitles.findIndex((t) => t.id === configValue);
      // Borne qui ne désigne aucun titre du document (sommaire venu d'une
      // section ou d'un autre document, titre supprimé) : comme sans borne.
      // Sinon elle retombait sur l'avant-dernier titre et les listes
      // « Du titre » / « Au titre » refusaient tout choix.
      if (configIndex === -1) { return defaultValue }
      // if config has changed title with lower level can be hidden
      // but inherited from previous config
      const previousTitleId = allTitles.slice(0, configIndex)
        .reverse()
        .find((t) => displayableTitles.map((t) => t.id).includes(t.id))
        ?.id ?? defaultValue;
      if (start) {
        // previous title that can be displayed if start
        return previousTitleId;
      } else {
        // next title that can be displayed if end
        const nextTitle = allTitles.slice(allTitles.findIndex((t) => t.id === configValue))
          .find((t) => displayableTitles.includes(t.id));
        return nextTitle ? nextTitle.id : previousTitleId
      }
    }
    if (start && startFrom) {
      return displayableTitles[startFrom].id
    }
    if (!start && duppedBreakIdx) {
      return displayableTitles[~~startFrom + duppedBreakIdx - 1].id
    }
    return defaultValue
  }

  return {
    idStart: getId(startConfigValue, displayableTitles[0].id),
    idEnd: getId(endConfigValue, displayableTitles[displayableTitles.length - 1].id, false)
  }
}

// Titres affichés par un sommaire selon ses bornes. Chaque borne compte seule :
// une borne qui n'est pas un id de titre (nombre des anciens sommaires, ex.
// « 1 / 0 » posé par une section, ou null) vaut « sans borne » de ce côté.
// Avant, une fin à 0 faisait ignorer aussi le début choisi : le sommaire
// affichait tout le document quel que soit « Du titre ».
export const titlesWithinBounds = ({ lineSummaryStart, lineSummaryEnd, displayableTitles, allTitles, startFrom = null, duppedBreakIdx = null }) => {
  const hasBound = _.isString(lineSummaryStart) || _.isString(lineSummaryEnd);
  if (!hasBound && startFrom === null && duppedBreakIdx === null) return displayableTitles;

  const { idStart, idEnd } = getIdsFromConfig(lineSummaryStart, lineSummaryEnd, displayableTitles, allTitles, startFrom, duppedBreakIdx);
  const startTitleIndex = displayableTitles.findIndex((title) => title.id === idStart);
  const endTitleIndex = displayableTitles.findIndex((title) => title.id === idEnd);
  return displayableTitles.slice(startTitleIndex, endTitleIndex + 1);
};

// Bornes du sommaire après le choix d'un titre dans « Du titre » ou « Au titre ».
// Un choix qui dépasse l'autre borne la déplace sur le même titre, au lieu
// d'être ignoré sans rien dire.
export const boundsAfterChoice = ({ titles, idStart, idEnd, which, id }) => {
  const chosenIndex = titles.findIndex((title) => title.id === id);
  if (chosenIndex === -1) return null;
  if (which === 'start') {
    const endIndex = titles.findIndex((title) => title.id === idEnd);
    return { start: id, end: chosenIndex > endIndex ? id : idEnd };
  }
  const startIndex = titles.findIndex((title) => title.id === idStart);
  return { start: startIndex > chosenIndex ? id : idStart, end: id };
};

const selector = (id) => (state) => {
  const {
    boxes,
    updateBox,
    configuration,
    selectedBoxId,
    summaryConfiguration,
    columns,
    masks,
  } = state;
  const allTitles = boxes.filter((box) => isATitleBox(box.type));
  const selectedBox = boxes.find((b) => b.id === selectedBoxId)
  const displayableTitles = getDisplayableTitles(boxes, summaryConfiguration, configuration, columns, masks, selectedBox.content.configuration.visibleLevels)
  const { lineSummaryStart, lineSummaryEnd } = selectedBox.content.configuration
  const { idStart, idEnd } = getIdsFromConfig(lineSummaryStart, lineSummaryEnd, displayableTitles, allTitles)
  return {
    titles: displayableTitles,
    idStart,
    idEnd,
    updateBox: updateBox,
    separator: configuration.separatorTitle,
    summaryConfiguration,
  };
};

export const SummarySelector = ({ selectedBox }) => {
  var { updateBox, idStart, idEnd, titles } = useStore(
    selector(selectedBox.id)
  );
  const { t } = useTranslation();

  if (titles.length === 0) { return null };

  const applyChoice = (which, id) => {
    const bounds = boundsAfterChoice({ titles, idStart, idEnd, which, id });
    if (!bounds) return;
    updateBox(selectedBox?.id, (selectedBox) => {
      // L'autre borne n'est écrite que si elle a bougé : sinon elle resterait
      // « jusqu'à la fin » et suivrait les titres ajoutés ensuite.
      if (which === 'start' || bounds.start !== idStart) {
        selectedBox.content.configuration.lineSummaryStart = bounds.start;
      }
      if (which === 'end' || bounds.end !== idEnd) {
        selectedBox.content.configuration.lineSummaryEnd = bounds.end;
      }
    });
  }

  const selectStartTitle = (id) => applyChoice('start', id)

  const selectEndTitle = (id) => applyChoice('end', id)

  const options = titles.map(t => ({
    label: `${t.indexTitle} ${t.text}`,
    value: t.id
  }))

  // Largeur pleine plutôt que 200px fixes : le panel de droite est étroit, et
  // le libellé empilé au-dessus dit désormais lequel des deux borne le début.
  return (
    <>
      <StackedLabelField label={t('summary-start-title')}>
        <NavbarSelector
          key="summaryStartLineIndex"
          disabled={selectedBox?.type !== 'summary'}
          suffixIcon={<MenuOutlined />}
          onSelect={selectStartTitle}
          selectedValue={idStart}
          options={options}
          width='100%'
          popupMatchSelectWidth={false}
          popupClassName="summary-line-selector-popup"
          hideOptionTooltip
        />
      </StackedLabelField>
      <StackedLabelField label={t('summary-end-title')}>
        <NavbarSelector
          key="summaryEndLineIndex"
          disabled={selectedBox?.type !== 'summary'}
          suffixIcon={<MenuOutlined />}
          onSelect={selectEndTitle}
          selectedValue={idEnd}
          options={options}
          width='100%'
          popupMatchSelectWidth={false}
          popupClassName="summary-line-selector-popup"
          hideOptionTooltip
        />
      </StackedLabelField>
    </>
  );
};
